import * as React from "react";
import { useSubscription } from "../../hooks/useSubscription";
import { loader } from "graphql.macro";
import {map, pipe, scan} from "wonka";
import produce from "immer";
import { ProfileImage } from "../ProfileImage";
import { Trans, useTranslation } from "react-i18next";
import { useCountDown } from "../../hooks/useCountDown";
import { useBehaviourSubject, useSource } from "../../hooks/useSource";
import { useClient } from "urql";
import {
  CancelInvitationMutationMutation,
  CancelInvitationMutationMutationVariables,
} from "./graphql/CancelInvitationMutation.generated";
import { AbsoluteCenter } from "../AbsoluteCenter";
import { ButtonWithSound, useAudio } from "../AudioProvider";
import { useSyncedTimeProvider } from "../SyncedTimeProvider";
import styles from "../Area/TablesList/InstantTournaments.module.scss";
import classNames from "classnames";
import closeIcon from "../Area/TablesList/assets/close-icon.png";
import invitationLogo from "../Area/TablesList/assets/invitation-logo.png";
import coin from "../Area/TablesList/assets/coin.png";
import goldCoins from "../Area/TablesList/assets/gold-coins.png";
import players from "../Area/TablesList/assets/players-icon.png";
import formatIcon from "../Area/TablesList/assets/format.png";
import divider from "../Area/TablesList/assets/divider.png";
import { useMutation } from "../../hooks/useMutation";
import {
  PurchaseScheduledTournamentEntryMutation,
  PurchaseScheduledTournamentEntryMutationVariables,
} from "../Area/TablesList/graphql/JoinScheduledTournamentMutation.generated";
import { useHistory } from "react-router-dom";
import { TournamentType } from "../../graphql/generated";
import {
  ScheduledTournamentInvitationsSubscription,
  ScheduledTournamentInvitationsSubscriptionVariables,
} from "./graphql/ScheduledTournamentInvitationsSubscription.generated";

const JOIN_SCHEDULED_TOURNAMENT_QUERY = loader(
  "../Area/TablesList/graphql/JoinScheduledTournamentMutation.graphql"
);

const SCHEDULED_TOURNAMENT_INVITATIONS_SUBSCRIPTION = loader(
  "./graphql/ScheduledTournamentInvitationsSubscription.graphql"
);

const CANCEL_INVITATION_MUTATION = loader(
  "./graphql/CancelInvitationMutation.graphql"
);

const formatText = {
  ONE_VS_ONE: "1x1",
  TWO_VS_TWO: "2x2",
};

const AVATAR_SIZE = {
  width: 56,
  height: 56,
};

type Invitation = Omit<
  NonNullable<
    ScheduledTournamentInvitationsSubscription["scheduledTournamentInvitations"]
  >,
  "start" | "end"
> & {
  start: number;
  end: number;
};

type Message =
  | {
      type: "ADD_INVITATION";
      payload: Invitation;
    }
  | {
      type: "CLEAR_EXPIRED";
    }
  | {
      type: "POP";
    }
  | {
      type: "CLEAR";
    };

const reducer = produce((state: Array<Invitation>, message: Message) => {
  switch (message.type) {
    case "ADD_INVITATION":
      state.push(message.payload);
      return state;
    case "CLEAR_EXPIRED":
      const now = Date.now();
      return state.filter(({ end }) => end > now);
    case "POP":
      return state.slice(1);
    case "CLEAR":
      state = [];
      return state;
    default:
      return state;
  }
});

const TournamentInfoItem = ({
  image,
  text,
  value,
}: {
  image: string;
  text: string;
  value: string;
}) => {
  return (
    <div className="flex flex-col flex-grow gap-2">
      <div className="flex items-center justify-center gap-1">
        <img
          src={image}
          className={styles.tournamentInfoItemIcon}
          alt={"img"}
        />
        <span className="text-white text-base capitalize">{text}</span>
      </div>
      <img src={divider} alt={"img"} />
      <span className="text-yellow-400 text-lg">{value}</span>
    </div>
  );
};

const CloseButton = ({ closeHandler }: { closeHandler: () => void }) => {
  return (
    <div className="flex justify-center absolute right-0 top-0">
      <ButtonWithSound className="btn btn-lg p-0" onClick={closeHandler}>
        <img src={closeIcon} alt="close" />
      </ButtonWithSound>
    </div>
  );
};

const InvitationData = ({
  onCancel,
  onJoin,
  invitation,
  onTick,
}: {
  onCancel: (id: string) => void;
  onJoin: (id: string) => void;
  invitation: Invitation;
  onTick?: (remaining: number) => void;
}) => {
  const { remaining } = useCountDown(invitation.start, invitation.end);
  React.useEffect(() => {
    if (onTick !== undefined && remaining > 0) onTick(remaining);
  }, [onTick, remaining]);
  const { t } = useTranslation();

  const getMaxValue = () => {
    const awards = invitation.scheduledTournament?.awards || [];
    const maxValue = Math.max(...awards.map((a) => a.amount.value));

    return maxValue;
  };

  const isTwoOnTwo = invitation.scheduledTournament.playerPerTeam === "TWO_VS_TWO";
  const isPrivate = invitation.scheduledTournament.type === "PRIVATE";

  const invitationText = isPrivate
    ? "instant_tournament.invited_you_to_join_private_tournament"
    : isTwoOnTwo ? "instant_tournament.invited_you" : "instant_tournament.invited_you_simple";

  return (
    <div
      className={classNames(
        "flex flex-col relative bg-blue-800",
        styles.tournamentInvitation
      )}
    >
      <div className="flex justify-between items-center px-8 pt-3 pb-2 gap-8 absolute left-0 top-0 w-full">
        <div className="flex items-center gap-3 relative w-full">
          <div className={styles.invitationPhoto}>
            <ProfileImage
              id={invitation.from.id}
              name={invitation.from.name}
              className="bg-gray-400 rounded-full"
              {...AVATAR_SIZE}
            />
          </div>
          <img
            src={invitationLogo}
            className={styles.invitationLogo}
            alt="New tournament"
          />
          <CloseButton closeHandler={() => onCancel(invitation.id)} />
        </div>
      </div>

      <div className="flex flex-col gap-8">
        <div className="flex flex-col px-8 gap-4 items-center text-center">
          <div className="flex flex-col gap-1 items-center text-center text-white text-base">
            <span className="font-semibold text-yellow-400 text-2xl capitalize">
              {t("instant_tournament.tournament_invitation")}
            </span>
            <p>
              <Trans
                className="flex"
                i18nKey={invitationText}
                components={{ name: <span className="font-bold mr-1" /> }}
                values={{
                  fromName: invitation.from.name,
                  tournamentName: invitation.scheduledTournament?.name,
                }}
              />
            </p>
            <span>{t("instant_tournament.accept_and_join_tournament")}</span>
          </div>
          <div
            className={classNames(
              "flex w-full rounded-lg gap-2",
              styles.tournamentInfo
            )}
          >
            <TournamentInfoItem
              value={String(getMaxValue())}
              text={t("instant_tournament.entry")}
              image={coin}
            />
            <TournamentInfoItem
              value={String(invitation.scheduledTournament.price.value)}
              text={t("prize")}
              image={goldCoins}
            />
            <TournamentInfoItem
              value={String(invitation.scheduledTournament?.numberOfPlayers)}
              text={t("players")}
              image={players}
            />
            <TournamentInfoItem
              value={formatText[invitation.scheduledTournament?.playerPerTeam]}
              text={t("instant_tournament.format")}
              image={formatIcon}
            />
          </div>
        </div>
        <div className="flex justify-center gap-4">
          <ButtonWithSound
            onClick={() => onJoin(invitation.scheduledTournament.id)}
            className={classNames(
              "px-4",
              "text-white",
              "rounded-md",
              "font-semibold",
              "btn",
              "btn-green-600",
              "w-4/12",
              "text-base"
            )}
          >
            {t("instant_tournament.join_now")}
          </ButtonWithSound>
          <ButtonWithSound
            type="button"
            disabled={false}
            onClick={() => onCancel(invitation.scheduledTournament.id)}
            className="btn w-4/12 btn-gray-500 rounded-md gap-2 px-6 flex-shrink-0 text-base font-semibold text-black"
          >
            {t("deny")}
          </ButtonWithSound>
        </div>
      </div>
    </div>
  );
};

const Popup = ({
  invitations,
  onCancel,
  onJoin,
}: {
  invitations: Array<Invitation>;
  onCancel: (id: string) => void;
  onJoin: (id: string) => void;
}) => {
  const audio = useAudio();
  return (
    <div className="absolute bg-black-alpha-30 top-0 left-0 right-0 bottom-0 z-50">
      <AbsoluteCenter>
        <InvitationData
          invitation={invitations[0]}
          onCancel={onCancel}
          onJoin={onJoin}
          onTick={() => audio.play("timer")}
        />
      </AbsoluteCenter>
    </div>
  );
};

export const ScheduledTournamentInvitations = () => {
  const { push } = useHistory();
  const syncedTime = useSyncedTimeProvider();
  const [{ data }] = useSubscription<
    unknown,
    ScheduledTournamentInvitationsSubscription,
    ScheduledTournamentInvitationsSubscriptionVariables
  >({
    query: SCHEDULED_TOURNAMENT_INVITATIONS_SUBSCRIPTION,
    throwOnError: false,
  });

  const [, joinInstantTournament] = useMutation<
    PurchaseScheduledTournamentEntryMutation,
    PurchaseScheduledTournamentEntryMutationVariables
  >(JOIN_SCHEDULED_TOURNAMENT_QUERY, false);

  const handleJoinTournament = (id: string) => {
    const invitationId =
      data?.scheduledTournamentInvitations?.scheduledTournament.type ===
      TournamentType.Public
        ? data?.scheduledTournamentInvitations.id
        : null;

    joinInstantTournament({
      input: {
        scheduledTournamentId: id,
        invitationId: invitationId,
      },
    }).then((result) => {
      if (result.error) {
        return result.error;
      } else {
        update({ type: "CLEAR" });
        push(
          `/area/${data?.scheduledTournamentInvitations?.scheduledTournament.roomId}:${data?.scheduledTournamentInvitations?.scheduledTournament.gameId}/scheduled-tournaments/${id}`
        );
      }
    });
  };

  const [subscription$$, update] = useBehaviourSubject(
    React.useMemo<Message | null>(
      () =>
        data?.scheduledTournamentInvitations
          ? {
              type: "ADD_INVITATION",
              payload: {
                ...data?.scheduledTournamentInvitations,
                start: new Date(
                  data.scheduledTournamentInvitations.start
                ).getTime(),
                end: new Date(
                  data.scheduledTournamentInvitations.end
                ).getTime(),
              },
            }
          : null,
      [data]
    )
  );

  const state = useSource(
    React.useMemo(() => {
      return pipe(
        subscription$$,
        scan<Message | null, Array<Invitation>>((previous, message) => {
          if (message === null) return previous;
          return reducer(previous, message!);
        }, [])
      );
    }, [subscription$$]),
    []
  );

  const client = useClient();

  const onCancel = React.useCallback(
    (id: string) => {
      update({ type: "POP" });
      //No need to respond to success.Its a fire and forget mutation
      client
        .mutation<
          CancelInvitationMutationMutation,
          CancelInvitationMutationMutationVariables
        >(CANCEL_INVITATION_MUTATION, { id })
        .toPromise()
        .catch((e) => {
          console.error("Error canceling invitation", e);
        });
    },
    [client, update]
  );

  return (
    <>
      {state.length && (
        // @ts-ignore
        <Popup
          invitations={state}
          onCancel={onCancel}
          onJoin={handleJoinTournament}
        />
      )}
    </>
  );
};
